/**
 * Advanced graph layout algorithms for React Flow
 * Implements Dagre, Force-Directed, and Radial layouts with domain-aware grouping
 */

import dagre from 'dagre'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'

// ============================================================================
// TYPES
// ============================================================================

export type LayoutAlgorithm = 'dagre' | 'force' | 'radial' | 'optimized'
export type LayoutDirection = 'TB' | 'BT' | 'LR' | 'RL'

export interface LayoutConfig {
  algorithm: LayoutAlgorithm
  direction?: LayoutDirection
  nodeSpacing?: number
  rankSpacing?: number
  edgeLength?: number
  groupPadding?: number
  enableDomainClustering?: boolean
}

export interface GraphNode {
  uri: string
  label: string
  domain: OntologyDomain
  properties?: number
  instanceCount?: number
}

export interface GraphEdge {
  source: string
  target: string
  label?: string
}

export interface LayoutResult {
  positions: Record<string, { x: number; y: number }>
  domainBounds?: Record<OntologyDomain, { x: number; y: number; width: number; height: number }>
}

// ============================================================================
// DAGRE HIERARCHICAL LAYOUT
// ============================================================================

/**
 * Dagre hierarchical layout with domain-aware grouping
 * Best for: Schema view with clear hierarchy and minimal edge crossings
 */
export function calculateDagreLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  config: LayoutConfig = {}
): LayoutResult {
  const {
    direction = 'LR',
    nodeSpacing = 80,
    rankSpacing = 200,
    enableDomainClustering = true,
  } = config

  // Create dagre graph
  const g = new dagre.graphlib.Graph()
  g.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    marginx: 50,
    marginy: 50,
  })

  g.setDefaultEdgeLabel(() => ({}))

  // Add nodes with domain-based constraints
  const NODE_WIDTH = 200
  const NODE_HEIGHT = 80

  if (enableDomainClustering) {
    // Group nodes by domain and assign rank hints
    const domainRanks: Record<OntologyDomain, number> = {
      core: 0,
      market: 1,
      business: 2,
      solution: 3,
    }

    nodes.forEach((node) => {
      g.setNode(node.uri, {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        // Use rank to encourage domain clustering
        rank: domainRanks[node.domain],
      })
    })
  } else {
    nodes.forEach((node) => {
      g.setNode(node.uri, {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      })
    })
  }

  // Add edges
  edges.forEach((edge) => {
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target)
    }
  })

  // Run dagre layout
  dagre.layout(g)

  // Extract positions
  const positions: Record<string, { x: number; y: number }> = {}
  nodes.forEach((node) => {
    const dagreNode = g.node(node.uri)
    if (dagreNode) {
      positions[node.uri] = {
        x: dagreNode.x - NODE_WIDTH / 2,
        y: dagreNode.y - NODE_HEIGHT / 2,
      }
    }
  })

  // Calculate domain bounds if clustering is enabled
  let domainBounds: Record<OntologyDomain, { x: number; y: number; width: number; height: number }> | undefined

  if (enableDomainClustering) {
    domainBounds = calculateDomainBounds(nodes, positions, NODE_WIDTH, NODE_HEIGHT)
  }

  return { positions, domainBounds }
}

// ============================================================================
// FORCE-DIRECTED LAYOUT (D3-style)
// ============================================================================

/**
 * Force-directed layout with domain-based clustering
 * Best for: Knowledge graph view with natural clustering
 */
export function calculateForceDirectedLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  config: LayoutConfig = {}
): LayoutResult {
  const {
    edgeLength = 150,
    enableDomainClustering = true,
    nodeSpacing = 100,
  } = config

  const positions: Record<string, { x: number; y: number }> = {}

  if (nodes.length === 0) return { positions }

  // Initialize positions randomly or by domain
  const centerX = 500
  const centerY = 400

  if (enableDomainClustering) {
    // Position nodes in domain-specific regions
    const domainCenters: Record<OntologyDomain, { x: number; y: number }> = {
      core: { x: centerX - 300, y: centerY - 200 },
      market: { x: centerX - 150, y: centerY },
      business: { x: centerX + 150, y: centerY },
      solution: { x: centerX + 300, y: centerY + 200 },
    }

    nodes.forEach((node, i) => {
      const domainCenter = domainCenters[node.domain]
      const angle = (2 * Math.PI * i) / nodes.length
      const radius = 80
      positions[node.uri] = {
        x: domainCenter.x + radius * Math.cos(angle) + (Math.random() - 0.5) * 20,
        y: domainCenter.y + radius * Math.sin(angle) + (Math.random() - 0.5) * 20,
      }
    })
  } else {
    // Random initialization
    nodes.forEach((node) => {
      positions[node.uri] = {
        x: centerX + (Math.random() - 0.5) * 800,
        y: centerY + (Math.random() - 0.5) * 600,
      }
    })
  }

  // Build adjacency structure for forces
  const adjacency: Record<string, Set<string>> = {}
  nodes.forEach((node) => {
    adjacency[node.uri] = new Set()
  })
  edges.forEach((edge) => {
    adjacency[edge.source]?.add(edge.target)
    adjacency[edge.target]?.add(edge.source)
  })

  // Simulate forces (simplified version - in production use d3-force)
  // Reduced iterations for better performance (50 instead of 100)
  const iterations = 50
  const coolingFactor = 0.9
  let temperature = 100

  for (let iter = 0; iter < iterations; iter++) {
    const forces: Record<string, { x: number; y: number }> = {}

    // Initialize forces
    nodes.forEach((node) => {
      forces[node.uri] = { x: 0, y: 0 }
    })

    // Repulsive forces between all nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const nodeA = nodes[i]
        const nodeB = nodes[j]
        const posA = positions[nodeA.uri]
        const posB = positions[nodeB.uri]

        const dx = posB.x - posA.x
        const dy = posB.y - posA.y
        const distance = Math.sqrt(dx * dx + dy * dy) || 1

        // Stronger repulsion for same domain if clustering enabled
        const repulsionStrength = enableDomainClustering && nodeA.domain === nodeB.domain
          ? nodeSpacing * 0.8
          : nodeSpacing

        const force = (repulsionStrength * repulsionStrength) / distance

        const fx = (dx / distance) * force
        const fy = (dy / distance) * force

        forces[nodeA.uri].x -= fx
        forces[nodeA.uri].y -= fy
        forces[nodeB.uri].x += fx
        forces[nodeB.uri].y += fy
      }
    }

    // Attractive forces along edges
    edges.forEach((edge) => {
      const posSource = positions[edge.source]
      const posTarget = positions[edge.target]

      if (!posSource || !posTarget) return

      const dx = posTarget.x - posSource.x
      const dy = posTarget.y - posSource.y
      const distance = Math.sqrt(dx * dx + dy * dy) || 1

      const force = (distance - edgeLength) * 0.1

      const fx = (dx / distance) * force
      const fy = (dy / distance) * force

      forces[edge.source].x += fx
      forces[edge.source].y += fy
      forces[edge.target].x -= fx
      forces[edge.target].y -= fy
    })

    // Domain clustering forces (attract nodes to domain centers)
    if (enableDomainClustering) {
      const domainCenters: Record<OntologyDomain, { x: number; y: number }> = {
        core: { x: centerX - 300, y: centerY - 200 },
        market: { x: centerX - 150, y: centerY },
        business: { x: centerX + 150, y: centerY },
        solution: { x: centerX + 300, y: centerY + 200 },
      }

      nodes.forEach((node) => {
        const pos = positions[node.uri]
        const domainCenter = domainCenters[node.domain]

        const dx = domainCenter.x - pos.x
        const dy = domainCenter.y - pos.y

        // Gentle pull towards domain center
        forces[node.uri].x += dx * 0.02
        forces[node.uri].y += dy * 0.02
      })
    }

    // Apply forces with cooling
    nodes.forEach((node) => {
      const force = forces[node.uri]
      const displacement = Math.sqrt(force.x * force.x + force.y * force.y)

      if (displacement > 0) {
        const limitedDisplacement = Math.min(displacement, temperature)
        positions[node.uri].x += (force.x / displacement) * limitedDisplacement
        positions[node.uri].y += (force.y / displacement) * limitedDisplacement
      }
    })

    temperature *= coolingFactor
  }

  const domainBounds = enableDomainClustering
    ? calculateDomainBounds(nodes, positions, 200, 80)
    : undefined

  return { positions, domainBounds }
}

// ============================================================================
// OPTIMIZED COLUMNAR LAYOUT (Original, improved)
// ============================================================================

/**
 * Optimized columnar layout by domain
 * Best for: Schema view with clear domain separation
 */
export function calculateOptimizedLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  config: LayoutConfig = {}
): LayoutResult {
  const {
    nodeSpacing = 90,
    rankSpacing = 180,
  } = config

  const NODE_HEIGHT = nodeSpacing
  const COLUMN_WIDTH = 280
  const DOMAIN_GAP = rankSpacing

  // Group by domain
  const byDomain: Record<OntologyDomain, GraphNode[]> = {
    core: [],
    market: [],
    business: [],
    solution: [],
  }

  nodes.forEach((node) => byDomain[node.domain].push(node))

  // Build edge count map
  const edgeCount: Record<string, number> = {}
  nodes.forEach((node) => {
    edgeCount[node.uri] = 0
  })
  edges.forEach((edge) => {
    if (edgeCount[edge.source] !== undefined) edgeCount[edge.source]++
  })

  // Sort within each domain by connectivity and instance count
  const sortedByDomain: Record<OntologyDomain, GraphNode[]> = {
    core: [],
    market: [],
    business: [],
    solution: [],
  }

  ;(['core', 'market', 'business', 'solution'] as OntologyDomain[]).forEach((domain) => {
    sortedByDomain[domain] = byDomain[domain].sort((a, b) => {
      const aScore = (edgeCount[a.uri] || 0) + (a.instanceCount || 0) * 0.1
      const bScore = (edgeCount[b.uri] || 0) + (b.instanceCount || 0) * 0.1
      return bScore - aScore
    })
  })

  const positions: Record<string, { x: number; y: number }> = {}

  // Calculate max height per domain
  const domainHeights = {
    core: sortedByDomain.core.length * NODE_HEIGHT,
    market: sortedByDomain.market.length * NODE_HEIGHT,
    business: sortedByDomain.business.length * NODE_HEIGHT,
    solution: sortedByDomain.solution.length * NODE_HEIGHT,
  }

  const maxHeight = Math.max(...Object.values(domainHeights))

  // Position nodes - center each domain column vertically
  ;(['core', 'market', 'business', 'solution'] as OntologyDomain[]).forEach((domain, domainIndex) => {
    const domainNodes = sortedByDomain[domain]
    const domainHeight = domainNodes.length * NODE_HEIGHT
    const yOffset = (maxHeight - domainHeight) / 2

    domainNodes.forEach((node, index) => {
      positions[node.uri] = {
        x: domainIndex * (COLUMN_WIDTH + DOMAIN_GAP),
        y: yOffset + index * NODE_HEIGHT,
      }
    })
  })

  const domainBounds = calculateDomainBounds(nodes, positions, COLUMN_WIDTH, NODE_HEIGHT)

  return { positions, domainBounds }
}

// ============================================================================
// RADIAL LAYOUT (Improved)
// ============================================================================

/**
 * Radial layout based on connection distance from central node
 * Best for: Exploring relationships from a focal point
 */
export function calculateRadialLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  config: LayoutConfig = {},
  centerNodeUri?: string
): LayoutResult {
  const positions: Record<string, { x: number; y: number }> = {}

  if (nodes.length === 0) return { positions }

  const centerX = 500
  const centerY = 400

  // Build adjacency list
  const adjacency: Record<string, Set<string>> = {}
  nodes.forEach((node) => {
    adjacency[node.uri] = new Set()
  })
  edges.forEach((edge) => {
    adjacency[edge.source]?.add(edge.target)
    adjacency[edge.target]?.add(edge.source)
  })

  // Find most connected node as center
  let selectedCenterUri = centerNodeUri || nodes[0]?.uri
  if (!centerNodeUri) {
    let maxConnections = 0
    nodes.forEach((node) => {
      const connections = adjacency[node.uri]?.size || 0
      if (connections > maxConnections) {
        maxConnections = connections
        selectedCenterUri = node.uri
      }
    })
  }

  // BFS to calculate distances
  const distances: Record<string, number> = {}
  const visited = new Set<string>()
  const queue: Array<{ uri: string; distance: number }> = [{ uri: selectedCenterUri, distance: 0 }]
  visited.add(selectedCenterUri)

  while (queue.length > 0) {
    const { uri, distance } = queue.shift()!
    distances[uri] = distance

    adjacency[uri]?.forEach((neighbor) => {
      if (!visited.has(neighbor)) {
        visited.add(neighbor)
        queue.push({ uri: neighbor, distance: distance + 1 })
      }
    })
  }

  // Handle disconnected nodes
  const maxDistance = Math.max(...Object.values(distances), 0)
  nodes.forEach((node) => {
    if (distances[node.uri] === undefined) {
      distances[node.uri] = maxDistance + 1
    }
  })

  // Group by ring (distance)
  const byRing: Record<number, GraphNode[]> = {}
  nodes.forEach((node) => {
    const ring = distances[node.uri]
    if (!byRing[ring]) byRing[ring] = []
    byRing[ring].push(node)
  })

  // Position nodes in concentric circles
  const baseRadius = 120
  const ringSpacing = 140

  Object.entries(byRing).forEach(([ringStr, ringNodes]) => {
    const ring = parseInt(ringStr)

    if (ring === 0) {
      // Center node
      positions[ringNodes[0].uri] = { x: centerX, y: centerY }
    } else {
      const radius = baseRadius + (ring - 1) * ringSpacing
      const angleOffset = (ring * Math.PI) / 6

      ringNodes.forEach((node, index) => {
        const angle = angleOffset + (2 * Math.PI * index) / ringNodes.length - Math.PI / 2
        positions[node.uri] = {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
        }
      })
    }
  })

  return { positions }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate bounding boxes for each domain
 */
function calculateDomainBounds(
  nodes: GraphNode[],
  positions: Record<string, { x: number; y: number }>,
  nodeWidth: number,
  nodeHeight: number
): Record<OntologyDomain, { x: number; y: number; width: number; height: number }> {
  const byDomain: Record<OntologyDomain, GraphNode[]> = {
    core: [],
    market: [],
    business: [],
    solution: [],
  }

  nodes.forEach((node) => {
    if (positions[node.uri]) {
      byDomain[node.domain].push(node)
    }
  })

  const bounds: Record<OntologyDomain, { x: number; y: number; width: number; height: number }> = {
    core: { x: 0, y: 0, width: 0, height: 0 },
    market: { x: 0, y: 0, width: 0, height: 0 },
    business: { x: 0, y: 0, width: 0, height: 0 },
    solution: { x: 0, y: 0, width: 0, height: 0 },
  }

  const padding = 40

  ;(['core', 'market', 'business', 'solution'] as OntologyDomain[]).forEach((domain) => {
    const domainNodes = byDomain[domain]

    if (domainNodes.length === 0) return

    const xs = domainNodes.map((n) => positions[n.uri].x)
    const ys = domainNodes.map((n) => positions[n.uri].y)

    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)

    bounds[domain] = {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + nodeWidth + padding * 2,
      height: maxY - minY + nodeHeight + padding * 2,
    }
  })

  return bounds
}

/**
 * Main layout function that routes to appropriate algorithm
 */
export function calculateLayout(
  nodes: GraphNode[],
  edges: GraphEdge[],
  config: LayoutConfig
): LayoutResult {
  switch (config.algorithm) {
    case 'dagre':
      return calculateDagreLayout(nodes, edges, config)
    case 'force':
      return calculateForceDirectedLayout(nodes, edges, config)
    case 'radial':
      return calculateRadialLayout(nodes, edges, config)
    case 'optimized':
    default:
      return calculateOptimizedLayout(nodes, edges, config)
  }
}
